import * as React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { useForm } from "react-hook-form";
import { object, string, ref } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { resetPasswordConfirmFn } from "../APIServices/Services";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "../ui/Spinner";

const useStyles = makeStyles((themes) => ({
  changePasswordButton: {
    backgroundColor: themes.palette.common.blue,
  },
  "&:hover": {
    backgroundColor: themes.palette.common.blue,
  },
}));

const theme = createTheme();

export default function ChangePassword() {
  const params = useParams();
  const uid = params.uid;
  const token = params.token;
  const validateSchema = object().shape({
    password1: string()
      .min(1, "Password is required")
      .min(8, "Password must be more than 8 characters")
      .max(32, "Password must be less than 32 characters")
      .matches(
        /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
        "Password must contain at least 8 characters, one uppercase, one number and one special case character"
      ),
    password2: string()
      .min(1, "Please confirm your password")
      .min(8, "Password must be more than 8 characters")
      .max(32, "Password must be less than 32 characters")
      .matches(
        /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
        "Password must contain at least 8 characters, one uppercase, one number and one special case character"
      )
      .when("password", {
        is: (password) => (password && password.length > 0 ? true : false),
        then: string().oneOf([ref("password")], "Passwords do not match"),
      }),
  });

  const methods = useForm({
    resolver: yupResolver(validateSchema),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [reset, isSubmitSuccessful]);

  const navigate = useNavigate();

  const { mutate, isLoading, isError, error } = useMutation(
    (password1, password2, uid, token) => {
      resetPasswordConfirmFn(password1, password2, uid, token);
    },
    {
      onSuccess: (data) => {
        setTimeout(() => {
          navigate("/login");
        }, 10000);
      },
    }
  );

  const onSubmitHandler = (password1, password2) => {
    mutate(password1, password2, uid, token);
  };

  const classes = useStyles();

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: "2em",
          }}
        >
          <Typography component="h1" variant="h4">
            Change Password
          </Typography>
          {isError && (
            <Typography component="h1" variant="h4">
              An error occurred: {error}
            </Typography>
          )}
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmitHandler)}
            sx={{ mt: 3 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  type="password"
                  name="password"
                  label="Password"
                  {...register("password1")}
                  error={errors.password ? true : false}
                  id="password"
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  type="password"
                  name="confirmPassword"
                  label="Confirm Password"
                  {...register("password2")}
                  error={errors.confirmPassword ? true : false}
                  id="password"
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              className={classes.changePasswordButton}
              disabled={isLoading}
            >
              Change Password
            </Button>
            {isLoading && <Spinner />}
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
}
